<template>
	<div class="component-section" @click="handleClick">
		<h3 class="section-title">Messages & Feedback</h3>
		<div class="messages-content">
			<div class="messages-section">
				<label class="field-label">Messages</label>
				<div class="messages-list">
					<Message severity="info" :closable="false">Info message content</Message>
					<Message severity="success" :closable="false">Success message content</Message>
					<Message severity="warn" :closable="false">Warning message content</Message>
					<Message severity="error" :closable="false">Error message content</Message>
				</div>
			</div>

			<div class="progress-section">
				<label class="field-label">Progress Indicators</label>
				<div class="progress-items">
					<div class="progress-item">
						<label class="small-label">ProgressBar</label>
						<ProgressBar :value="progressValue" style="height: 1.5rem;">
							<span class="progress-text">{{ progressValue }}%</span>
						</ProgressBar>
					</div>
					<div class="progress-item">
						<label class="small-label">ProgressSpinner</label>
						<ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
					</div>
				</div>
			</div>

			<div class="toast-section">
				<label class="field-label">Toast Examples</label>
				<div class="toast-examples">
					<div class="toast-preview toast-info" @click="handleClick">
						<div class="toast-icon">
							<i class="pi pi-info-circle"></i>
						</div>
						<div class="toast-content">
							<div class="toast-summary">Info</div>
							<div class="toast-detail">This is an info toast message</div>
						</div>
						<div class="toast-close">
							<i class="pi pi-times"></i>
						</div>
					</div>
					
					<div class="toast-preview toast-success" @click="handleClick">
						<div class="toast-icon">
							<i class="pi pi-check"></i>
						</div>
						<div class="toast-content">
							<div class="toast-summary">Success</div>
							<div class="toast-detail">Operation completed successfully</div>
						</div>
						<div class="toast-close">
							<i class="pi pi-times"></i>
						</div>
					</div>
					
					<div class="toast-preview toast-warn" @click="handleClick">
						<div class="toast-icon">
							<i class="pi pi-exclamation-triangle"></i>
						</div>
						<div class="toast-content">
							<div class="toast-summary">Warning</div>
							<div class="toast-detail">This is a warning message</div>
						</div>
						<div class="toast-close">
							<i class="pi pi-times"></i>
						</div>
					</div>
					
					<div class="toast-preview toast-error" @click="handleClick">
						<div class="toast-icon">
							<i class="pi pi-times-circle"></i>
						</div>
						<div class="toast-content">
							<div class="toast-summary">Error</div>
							<div class="toast-detail">Something went wrong</div>
						</div>
						<div class="toast-close">
							<i class="pi pi-times"></i>
						</div>
					</div>
				</div>
			</div>

			<div class="dialog-section">
				<label class="field-label">Dialog Example</label>
				<div class="dialog-preview">
					<div class="dialog-overlay">
						<div class="dialog-container">
							<div class="dialog-header">
								<span class="dialog-title">Sample Dialog</span>
								<button class="dialog-close-btn">
									<i class="pi pi-times"></i>
								</button>
							</div>
							<div class="dialog-content">
								<p>This is a sample dialog to demonstrate the overlay styling in PrimeVue.</p>
							</div>
							<div class="dialog-footer">
								<button class="dialog-btn dialog-btn-secondary">Cancel</button>
								<button class="dialog-btn dialog-btn-primary">Save</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>


	</div>
</template>

<script setup lang="ts">
import { ref } from "vue"
import Message from "primevue/message"
import ProgressBar from "primevue/progressbar"
import ProgressSpinner from "primevue/progressspinner"

const emit = defineEmits<{
	(e: "elementClick", event: MouseEvent): void
}>()

const progressValue = ref(65)

const handleClick = (event: MouseEvent) => {
	emit("elementClick", event)
}
</script>

<style lang="scss" scoped>
.component-section {
	background: white;
	border-radius: 8px;
	padding: 1.5rem;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	margin-bottom: 1rem;
}

.section-title {
	font-size: 1.2rem;
	font-weight: 600;
	margin-bottom: 1rem;
	color: #374151;
	border-bottom: 2px solid #e5e7eb;
	padding-bottom: 0.5rem;
}

.messages-content {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.messages-section,
.progress-section,
.toast-section,
.dialog-section {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.field-label {
	font-size: 0.875rem;
	font-weight: 500;
	color: #6b7280;
}

.small-label {
	font-size: 0.75rem;
	font-weight: 400;
	color: #9ca3af;
}

.messages-list {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.progress-items {
	display: grid;
	grid-template-columns: 3fr 1fr;
	gap: 1rem;
}

.progress-item {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.flex {
	display: flex;
}

.gap-2 {
	gap: 0.5rem;
}

.toast-examples {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.75rem;
}

.toast-preview {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	padding: 1rem;
	border-radius: 6px;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
	border: 1px solid transparent;
	min-width: 20rem;
	max-width: 28rem;
	cursor: pointer;
}

.toast-icon {
	flex-shrink: 0;
	width: 1.25rem;
	height: 1.25rem;
	display: flex;
	align-items: center;
	justify-content: center;
}

.toast-content {
	flex: 1;
}

.toast-summary {
	font-weight: 600;
	font-size: 0.875rem;
	line-height: 1.25rem;
	margin-bottom: 0.25rem;
}

.toast-detail {
	font-size: 0.875rem;
	line-height: 1.25rem;
	opacity: 0.9;
}

.toast-close {
	flex-shrink: 0;
	width: 1rem;
	height: 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0.7;
	cursor: pointer;
}

.toast-info {
	background: var(--p-message-info-background);
	color: var(--p-message-info-color);
	border-color: var(--p-message-info-border-color);
}

.toast-info .toast-icon {
	color: var(--p-message-info-color);
}

.toast-success {
	background: var(--p-message-success-background);
	color: var(--p-message-success-color);
	border-color: var(--p-message-success-border-color);
}

.toast-success .toast-icon {
	color: var(--p-message-success-color);
}

.toast-warn {
	background: var(--p-message-warn-background);
	color: var(--p-message-warn-color);
	border-color: var(--p-message-warn-border-color);
}

.toast-warn .toast-icon {
	color: var(--p-message-warn-color);
}

.toast-error {
	background: var(--p-message-error-background);
	color: var(--p-message-error-color);
	border-color: var(--p-message-error-border-color);
}

.toast-error .toast-icon {
	color: var(--p-message-error-color);
}

.dialog-preview {
	position: relative;
	height: 300px;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid #e5e7eb;
}

.dialog-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: var(--p-overlay-mask-background, rgba(0, 0, 0, 0.4));
	display: flex;
	align-items: center;
	justify-content: center;
}

.dialog-container {
	background: var(--p-dialog-background, white);
	border-radius: var(--p-dialog-border-radius, 6px);
	box-shadow: var(--p-dialog-shadow, 0 4px 16px rgba(0, 0, 0, 0.15));
	min-width: 20rem;
	max-width: 90%;
	border: 1px solid var(--p-dialog-border-color, transparent);
}

.dialog-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1rem 1.25rem;
	border-bottom: 1px solid var(--p-dialog-header-border-color, #e5e7eb);
	background: var(--p-dialog-header-background, transparent);
}

.dialog-title {
	font-weight: 600;
	font-size: 1.125rem;
	color: var(--p-dialog-title-color, #374151);
}

.dialog-close-btn {
	background: transparent;
	border: none;
	color: var(--p-dialog-close-button-color, #6b7280);
	cursor: pointer;
	padding: 0.5rem;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2rem;
	height: 2rem;
	pointer-events: none;
}

.dialog-close-btn:hover {
	background: var(--p-dialog-close-button-hover-background, rgba(0, 0, 0, 0.04));
}

.dialog-content {
	padding: 1.25rem;
	color: var(--p-dialog-content-color, #374151);
}

.dialog-content p {
	margin: 0;
	line-height: 1.5;
}

.dialog-footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 0.5rem;
	padding: 1rem 1.25rem;
	border-top: 1px solid var(--p-dialog-header-border-color, #e5e7eb);
	background: var(--p-dialog-footer-background, transparent);
}

.dialog-btn {
	padding: 0.5rem 1rem;
	border-radius: var(--p-button-border-radius, 6px);
	font-size: 0.875rem;
	font-weight: 500;
	cursor: pointer;
	border: 1px solid transparent;
	transition: all 0.2s;
	pointer-events: none;
}

.dialog-btn-secondary {
	background: var(--p-button-secondary-background, transparent);
	color: var(--p-button-secondary-color, #6b7280);
	border-color: var(--p-button-secondary-border-color, #d1d5db);
}

.dialog-btn-primary {
	background: var(--p-button-primary-background, #3b82f6);
	color: var(--p-button-primary-color, white);
	border-color: var(--p-button-primary-border-color, #3b82f6);
}

.progress-item .p-progressbar {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
}

.progress-text {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	font-weight: 600;
	font-size: 0.875rem;
	z-index: 2;
	pointer-events: none;
}
</style>